import { useState, useEffect } from 'react';
import { X, Settings, Search, Package, Save, Zap, Edit3 } from 'lucide-react';
import { createConfiguration, updateConfiguration, getConfigurationByProduct } from '../../api/calculator';
import { getProducts } from '../../api/products';
import { getSizes } from '../../api/categories';

const CalculatorConfigModal = ({ product, onClose, onSave }) => {
  // Mode: 'dynamic' (auto-fill from size) or 'manual' (user enters values)
  const [configMode, setConfigMode] = useState('dynamic');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [existingConfig, setExistingConfig] = useState(null);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    pieces_per_carton: '',
    tile_length_mm: '',
    tile_width_mm: '',
    default_wastage_percent: 10
  });

  useEffect(() => {
    loadProducts();
    loadSizes();
    if (product && product.id) {
      setSelectedProduct(product);
      setSearchTerm(product.name || '');
      loadExistingConfig(product.id);
    }
  }, [product]);

  const loadProducts = async (search = '') => {
    try {
      setLoading(true);
      const response = await getProducts({ 
        search, 
        limit: 50,
        product_type: 'tile'
      });
      if (response.success) {
        setProducts(response.data || response.products || []);
      }
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSizes = async () => {
    try {
      const data = await getSizes();
      setSizes(data || []);
    } catch (error) {
      console.error('Failed to load sizes:', error);
    }
  };

  const loadExistingConfig = async (productId) => {
    if (!productId) return;

    try {
      const response = await getConfigurationByProduct(productId);
      if (response.success && response.data) {
        setExistingConfig(response.data);
        setFormData({
          pieces_per_carton: response.data.pieces_per_carton || '',
          tile_length_mm: response.data.tile_length_mm || '',
          tile_width_mm: response.data.tile_width_mm || '',
          default_wastage_percent: response.data.default_wastage_percent || 10
        });
      }
    } catch (error) {
      console.error('Failed to load config:', error);
    }
  };

  // Get size data for a product
  const getSizeDataForProduct = (prod) => {
    if (!prod || !prod.size_id) return null;
    return sizes.find(s => s.id === prod.size_id);
  };

  const handleProductSelect = (prod) => {
    setSelectedProduct(prod);
    setSearchTerm(prod.name || '');
    setShowProductDropdown(false);
    setExistingConfig(null);
    setError('');

    // In dynamic mode, auto-fill from size
    if (configMode === 'dynamic') {
      const sizeData = getSizeDataForProduct(prod);
      if (sizeData) {
        setFormData({
          pieces_per_carton: sizeData.default_pieces_per_carton || '',
          tile_length_mm: sizeData.tile_length_mm || '',
          tile_width_mm: sizeData.tile_width_mm || '',
          default_wastage_percent: 10
        });
      } else {
        // No size data available
        setFormData({
          pieces_per_carton: '',
          tile_length_mm: '',
          tile_width_mm: '',
          default_wastage_percent: 10
        });
        setError('No size configuration found for this product. Please configure the size category first or switch to Manual mode.');
      }
    } else {
      // Manual mode - reset fields
      setFormData({
        pieces_per_carton: '',
        tile_length_mm: '',
        tile_width_mm: '',
        default_wastage_percent: 10
      });
    }

    // Check for existing config
    if (prod.has_calculator_config && prod.id) {
      loadExistingConfig(prod.id);
    }
  };

  // Handle mode change
  const handleModeChange = (mode) => {
    setConfigMode(mode);
    setError('');
    
    if (selectedProduct && mode === 'dynamic') {
      // Re-apply auto-fill when switching to dynamic
      const sizeData = getSizeDataForProduct(selectedProduct);
      if (sizeData) {
        setFormData(prev => ({
          ...prev,
          pieces_per_carton: sizeData.default_pieces_per_carton || '',
          tile_length_mm: sizeData.tile_length_mm || '',
          tile_width_mm: sizeData.tile_width_mm || ''
        }));
      } else {
        setError('No size configuration found for this product. Please switch to Manual mode.');
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const calculateCoverage = () => {
    const { pieces_per_carton, tile_length_mm, tile_width_mm } = formData;
    if (pieces_per_carton && tile_length_mm && tile_width_mm) {
      const coverage = (parseFloat(tile_length_mm) * parseFloat(tile_width_mm) * parseFloat(pieces_per_carton)) / 1000000;
      return coverage.toFixed(4);
    }
    return '—';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!selectedProduct) {
      setError('Please select a product');
      return;
    }

    if (!formData.pieces_per_carton || !formData.tile_length_mm || !formData.tile_width_mm) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);

      const configData = {
        product_id: selectedProduct.id,
        pieces_per_carton: parseInt(formData.pieces_per_carton),
        tile_length_mm: parseFloat(formData.tile_length_mm),
        tile_width_mm: parseFloat(formData.tile_width_mm),
        default_wastage_percent: parseFloat(formData.default_wastage_percent)
      };

      let response;
      if (existingConfig) {
        response = await updateConfiguration(existingConfig.id, configData);
      } else {
        response = await createConfiguration(configData);
      }

      if (response.success) {
        onSave(response.data);
      }
    } catch (error) {
      console.error('Save failed:', error);
      setError(error.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const filteredProducts = products.filter(p => {
    if (p.product_type === 'accessory') return false;

    const term = (searchTerm || '').toLowerCase();
    const name = (p.name || '').toLowerCase();
    const code = (p.product_code || p.unique_code || '').toLowerCase();

    return name.includes(term) || code.includes(term);
  });

  // Check if fields should be read-only (dynamic mode with product selected)
  const isFieldReadOnly = configMode === 'dynamic' && selectedProduct;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden shadow-xl flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-lightGray">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-primary-black">
            <Settings className="w-5 h-5 text-accent-gold" />
            {existingConfig ? 'Update Calculator Configuration' : 'Configure Product for Calculator'}
          </h2>
          <button 
            className="w-9 h-9 flex items-center justify-center bg-secondary-offWhite rounded-lg text-primary-slate hover:bg-red-50 hover:text-red-500 transition-colors"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Mode Toggle */}
          <div className="flex items-center justify-center">
            <div className="flex bg-secondary-offWhite rounded-lg p-1">
              <button
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  configMode === 'dynamic' 
                    ? 'bg-white text-primary-black shadow-sm' 
                    : 'text-primary-slate hover:text-primary-black'
                }`}
                onClick={() => handleModeChange('dynamic')}
              >
                <Zap className="w-4 h-4" />
                Dynamic
              </button>
              <button
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  configMode === 'manual' 
                    ? 'bg-white text-primary-black shadow-sm' 
                    : 'text-primary-slate hover:text-primary-black'
                }`}
                onClick={() => handleModeChange('manual')}
              >
                <Edit3 className="w-4 h-4" />
                Manual
              </button>
            </div>
          </div>

          {/* Mode Description */}
          <div className={`text-center text-xs py-2 px-3 rounded-lg ${
            configMode === 'dynamic' 
              ? 'bg-blue-50 text-blue-600' 
              : 'bg-amber-50 text-amber-600'
          }`}>
            {configMode === 'dynamic' 
              ? '⚡ Auto-fills from product\'s size category configuration'
              : '✏️ Manually enter tile dimensions and carton details'
            }
          </div>

          {/* Product Selection */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-primary-black mb-2">
              <Package className="w-4 h-4 text-accent-gold" />
              Select Product <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-primary-slate" />
                <input
                  type="text"
                  placeholder="Search tile products..."
                  value={searchTerm || ''}
                  onChange={(e) => {
                    setSearchTerm(e.target.value || '');
                    loadProducts(e.target.value || '');
                  }}
                  onFocus={() => setShowProductDropdown(true)}
                  disabled={!!product}
                  className="w-full pl-10 pr-4 py-2.5 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </div>
              
              {showProductDropdown && !product && filteredProducts.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-secondary-lightGray rounded-lg shadow-lg max-h-36 overflow-y-auto">
                  {filteredProducts.map(prod => {
                    const sizeData = getSizeDataForProduct(prod);
                    return (
                      <div
                        key={prod.id}
                        className={`flex items-center gap-3 p-3 cursor-pointer border-b border-secondary-lightGray last:border-0 hover:bg-secondary-offWhite ${
                          selectedProduct?.id === prod.id ? 'bg-accent-gold/10' : ''
                        }`}
                        onClick={() => handleProductSelect(prod)}
                      >
                        <span className="font-bold text-accent-gold">
                          {prod.product_code || prod.unique_code}
                        </span>
                        <span className="text-primary-black">
                          {sizeData?.dimension || '—'}
                        </span>
                        <div className="flex-1 flex items-center justify-end gap-2">
                          {prod.has_calculator_config && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-600 text-xs rounded">Configured</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {selectedProduct && (
              <p className="text-xs text-green-600 mt-1">✓ Selected: {selectedProduct.name}</p>
            )}
          </div>

          {selectedProduct && (
            <>
              {/* Dynamic mode notice */}
              {configMode === 'dynamic' && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700 font-medium">Dynamic Configuration</p>
                  <p className="text-xs text-blue-600 mt-1">
                    Fields below are auto-filled from the size category. Only wastage is editable.
                  </p>
                </div>
              )}

              {/* Tile Dimensions */}
              <div>
                <h3 className="text-sm font-semibold text-primary-slate uppercase tracking-wide mb-3 pb-2 border-b border-secondary-lightGray">
                  Tile Dimensions
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-primary-black mb-1 block">
                      Tile Length (mm) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="tile_length_mm"
                      value={formData.tile_length_mm || ''}
                      onChange={handleInputChange}
                      placeholder="e.g., 600"
                      min="1"
                      step="1"
                      disabled={isFieldReadOnly}
                      className={`w-full px-4 py-2.5 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold ${
                        isFieldReadOnly ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''
                      }`}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-primary-black mb-1 block">
                      Tile Width (mm) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="tile_width_mm"
                      value={formData.tile_width_mm || ''}
                      onChange={handleInputChange}
                      placeholder="e.g., 600"
                      min="1"
                      step="1"
                      disabled={isFieldReadOnly}
                      className={`w-full px-4 py-2.5 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold ${
                        isFieldReadOnly ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''
                      }`}
                    />
                  </div>
                </div>
              </div>

              {/* Carton Details */}
              <div>
                <h3 className="text-sm font-semibold text-primary-slate uppercase tracking-wide mb-3 pb-2 border-b border-secondary-lightGray">
                  Carton Details
                </h3>
                <div>
                  <label className="text-sm font-medium text-primary-black mb-1 block">
                    Pieces per Carton <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="pieces_per_carton"
                    value={formData.pieces_per_carton || ''}
                    onChange={handleInputChange}
                    placeholder="e.g., 4"
                    min="1"
                    step="1"
                    disabled={isFieldReadOnly}
                    className={`w-full px-4 py-2.5 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold ${
                      isFieldReadOnly ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''
                    }`}
                  />
                </div>
              </div>

              {/* Wastage - Always Editable */}
              <div>
                <h3 className="text-sm font-semibold text-primary-slate uppercase tracking-wide mb-3 pb-2 border-b border-secondary-lightGray">
                  Default Settings
                </h3>
                <div>
                  <label className="text-sm font-medium text-primary-black mb-1 block">
                    Default Wastage Percentage
                  </label>
                  <div className="relative w-32">
                    <input
                      type="number"
                      name="default_wastage_percent"
                      value={formData.default_wastage_percent || 0}
                      onChange={handleInputChange}
                      min="0"
                      max="50"
                      step="1"
                      className="w-full px-4 py-2.5 pr-8 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold text-right"
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-primary-slate">%</span>
                  </div>
                  <p className="text-xs text-primary-slate mt-1">Recommended: 10% for standard calculations</p>
                </div>
              </div>

              {/* Calculated Coverage */}
              <div className="flex items-center justify-between p-4 bg-accent-gold/10 border border-accent-gold/30 rounded-lg">
                <span className="text-primary-slate">Coverage per Carton:</span>
                <span className="text-xl font-bold text-accent-gold">{calculateCoverage()} m²</span>
              </div>
            </>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-secondary-lightGray">
          <button 
            type="button" 
            className="flex-1 px-4 py-2.5 border border-secondary-lightGray text-primary-slate rounded-lg font-medium hover:bg-secondary-offWhite transition-colors"
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-accent-gold text-white rounded-lg font-semibold hover:bg-accent-goldDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={saving || !selectedProduct}
            onClick={handleSubmit}
          >
            {saving ? (
              'Saving...'
            ) : (
              <>
                <Save className="w-4 h-4" />
                {existingConfig ? 'Update' : 'Save'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CalculatorConfigModal;
